import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { Summary, SummaryItem } from '../types'

const SUMMARIES_QUERY_KEY = 'summaries'

export function useSummaryCache() {
  const queryClient = useQueryClient()

  // Update both list and individual summary caches optimistically
  const updateSummaryOptimistically = useCallback(
    (summaryId: string, updates: Partial<Summary>) => {
      // Cancel any outgoing refetches
      queryClient.cancelQueries({ queryKey: [SUMMARIES_QUERY_KEY] })
      queryClient.cancelQueries({ queryKey: [`summaries/${summaryId}`] })

      // Snapshot previous values for rollback
      const previousSummaries = queryClient.getQueryData([SUMMARIES_QUERY_KEY])
      const previousIndividualSummary = queryClient.getQueryData([`summaries/${summaryId}`])

      // Update list cache (SummaryItem array)
      queryClient.setQueryData([SUMMARIES_QUERY_KEY], (old: Array<SummaryItem>) => {
        if (!old) return old
        return old.map((item) =>
          item.id === summaryId ? { ...item, ...updates, updatedAt: new Date().toISOString() } : item,
        )
      })

      // Update individual summary cache (full Summary object)
      queryClient.setQueryData([`summaries/${summaryId}`], (old: Summary) => {
        if (!old) return old
        return { ...old, ...updates, updatedAt: new Date().toISOString() }
      })

      return {
        previousSummaries,
        previousIndividualSummary,
      }
    },
    [queryClient],
  )

  // Rollback optimistic updates on error
  const rollbackSummary = useCallback(
    (summaryId: string, context?: { previousSummaries?: unknown; previousIndividualSummary?: unknown }) => {
      if (context?.previousSummaries) {
        queryClient.setQueryData([SUMMARIES_QUERY_KEY], context.previousSummaries)
      }
      if (context?.previousIndividualSummary) {
        queryClient.setQueryData([`summaries/${summaryId}`], context.previousIndividualSummary)
      }
    },
    [queryClient],
  )

  // Invalidate summary queries
  const invalidateSummaryQueries = useCallback(
    (summaryId?: string) => {
      queryClient.invalidateQueries({ queryKey: [SUMMARIES_QUERY_KEY] })
      if (summaryId) {
        queryClient.invalidateQueries({ queryKey: [`summaries/${summaryId}`] })
      }
    },
    [queryClient],
  )

  // Invalidate all individual summary caches (useful for regenerate)
  const invalidateAllIndividualSummaries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        return (
          Array.isArray(query.queryKey) &&
          query.queryKey.length === 1 &&
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].startsWith('summaries/')
        )
      },
    })
  }, [queryClient])

  // Get summary from cache (tries individual first, then list)
  const getSummaryFromCache = useCallback(
    (summaryId: string): Summary | SummaryItem | undefined => {
      // Try individual cache first (has full data)
      const individual = queryClient.getQueryData([`summaries/${summaryId}`]) as Summary
      if (individual) return individual

      // Fallback to list cache
      const summaries = queryClient.getQueryData([SUMMARIES_QUERY_KEY]) as Array<SummaryItem>
      return summaries?.find((s) => s.id === summaryId)
    },
    [queryClient],
  )

  return {
    updateSummaryOptimistically,
    rollbackSummary,
    invalidateSummaryQueries,
    invalidateAllIndividualSummaries,
    getSummaryFromCache,
  }
}
